import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { sendAdminAlert, sendClientConfirmation } from '../lib/email'
import toast from 'react-hot-toast'

// ── STATE ABBREVIATIONS ───────────────────────────────────────
const ST_ABBR = { 'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA','Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Plus Jakarta Sans':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY' }

// ── EXCLUDED FROM BUNDLE DISCOUNT ────────────────────────────
const BUNDLE_EXCLUDE = ['Wedding Ceremony Officiation', 'Apostille']

// ── DEFAULT SERVICES ──────────────────────────────────────────
const DEFAULT_SERVICES = [
  { id:'aff-gen',   name:'Affidavit (General)',          client_price:75  },
  { id:'ack',       name:'Acknowledgement',              client_price:75  },
  { id:'jurat',     name:'Jurat / Oath',                 client_price:75  },
  { id:'poa',       name:'Power of Attorney',            client_price:100 },
  { id:'living',    name:'Living Will',                  client_price:100 },
  { id:'apostille', name:'Apostille',                    client_price:150 },
  { id:'wedding',   name:'Wedding Ceremony Officiation', client_price:200 },
]

// ── ADDRESS AUTOCOMPLETE ──────────────────────────────────────
function AddressAutocomplete({ value, onChange, onSelect, placeholder, inputClass }) {
  const [suggestions, setSuggestions] = useState([])
  const [open,        setOpen]        = useState(false)
  const [loading,     setLoading]     = useState(false)
  const timerRef = useRef(null)
  const justPickedRef = useRef(false)

  useEffect(() => {
    if (justPickedRef.current) { justPickedRef.current = false; setSuggestions([]); setOpen(false); return }
    if (!value || value.length < 4) { setSuggestions([]); setOpen(false); return }
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res  = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=us&q=${encodeURIComponent(value)}`, { headers:{ 'Accept-Language':'en-US' } })
        const data = await res.json()
        setSuggestions(data); setOpen(data.length > 0)
      } catch { setSuggestions([]) }
      finally { setLoading(false) }
    }, 400)
    return () => clearTimeout(timerRef.current)
  }, [value])

  function pick(item) {
    const a    = item.address || {}
    const road = a.road || a.pedestrian || a.footway || ''
    const num  = a.house_number || ''
    const addr = num ? `${num} ${road}` : road
    const city = a.city || a.town || a.village || a.hamlet || a.county || ''
    const st   = a.state || ''
    const zip  = a.postcode || ''
    const stAbbr = st.length === 2 ? st : (ST_ABBR[st] || st)
    justPickedRef.current = true
    onChange(addr)
    onSelect({ address:addr, city, state:stAbbr, zip })
    setSuggestions([]); setOpen(false)
  }

  return (
    <div style={{ position:'relative' }}>
      <input className={inputClass} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Start typing address...'} autoComplete="off"
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        aria-autocomplete="list" aria-label="Signing address" />
      {loading && <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', fontSize:11, color:'#4D4E8D' }}>🔍</span>}
      {open && suggestions.length > 0 && (
        <ul role="listbox" style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1.5px solid #4D4E8D', borderRadius:10, zIndex:999, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', marginTop:2, maxHeight:220, overflowY:'auto', listStyle:'none', padding:0 }}>
          {suggestions.map((s, i) => (
            <li key={i}>
              <button type="button" onMouseDown={() => pick(s)}
                style={{ width:'100%', padding:'10px 14px', background:'none', border:'none', cursor:'pointer', textAlign:'left', fontSize:12, color:'#151715', borderBottom:'1px solid #EEEFF1', fontFamily:'inherit' }}
                onMouseEnter={e => e.currentTarget.style.background='#EEF0F8'}
                onMouseLeave={e => e.currentTarget.style.background='none'}>
                📍 {s.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── STYLES ────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  .bk-skip { position:absolute; top:-100px; left:16px; background:#4D4E8D; color:#fff; padding:10px 20px; border-radius:8px; font-size:14px; font-weight:700; text-decoration:none; z-index:9999; transition:top .2s; }
  .bk-skip:focus { top:16px; }
  :focus-visible { outline:3px solid #4D4E8D; outline-offset:3px; border-radius:4px; }
  .bk-page { min-height:100vh; background:#F5F6F7; font-family:'Plus Jakarta Sans',sans-serif; display:flex; flex-direction:column; }
  .bk-nav { background:#FFFFFF; border-bottom:1px solid #E2E4E7; padding:14px 24px; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; z-index:100; }
  .bk-nav-brand { font-family:'Plus Jakarta Sans',sans-serif; font-size:20px; color:#4D4E8D; text-decoration:none; }
  .bk-nav-sub { font-size:8px; color:#8A8F94; letter-spacing:0.12em; display:block; }
  .bk-nav-back { color:#5C6065; font-size:12px; text-decoration:none; transition:color 0.2s; }
  .bk-nav-back:hover { color:#4D4E8D; }

  /* STEPPER */
  .bk-stepper { background:#fff; border-bottom:1px solid #E2E4E7; padding:16px 24px; display:flex; justify-content:center; }
  .bk-stepper-inner { display:flex; align-items:center; gap:0; max-width:600px; width:100%; }
  .bk-step { display:flex; flex-direction:column; align-items:center; flex:1; position:relative; }
  .bk-step::after { content:''; position:absolute; top:14px; left:50%; width:100%; height:2px; background:#E2E4E7; z-index:0; }
  .bk-step:last-child::after { display:none; }
  .bk-step-dot { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; position:relative; z-index:1; flex-shrink:0; transition:all 0.3s; }
  .bk-step-dot.done    { background:#5E8A72; color:#fff; }
  .bk-step-dot.active  { background:#4D4E8D; color:#fff; box-shadow:0 0 0 3px rgba(77,78,141,0.2); }
  .bk-step-dot.pending { background:#E2E4E7; color:#A2A6AA; }
  .bk-step-label { font-size:9px; color:#A2A6AA; margin-top:5px; text-align:center; letter-spacing:0.04em; font-weight:600; white-space:nowrap; }
  .bk-step-label.active  { color:#151715; }
  .bk-step-label.done    { color:#5E8A72; }

  /* CONTAINER */
  .bk-container { max-width:720px; margin:0 auto; padding:28px 20px 64px; flex:1; }
  .bk-page-title { font-family:'Plus Jakarta Sans',sans-serif; font-size:26px; color:#151715; font-weight:500; margin-bottom:4px; }
  .bk-page-sub { font-size:13px; color:#5C6065; margin-bottom:24px; line-height:1.7; }

  /* CARDS */
  .bk-card { background:#fff; border:1px solid #E2E4E7; border-radius:16px; overflow:hidden; margin-bottom:16px; }
  .bk-card-header { padding:14px 20px; background:#F5F6F7; border-bottom:1px solid #E2E4E7; display:flex; align-items:center; justify-content:space-between; }
  .bk-card-title { font-size:13px; font-weight:700; color:#151715; }
  .bk-card-body { padding:20px; }

  /* FORM */
  .bk-label { font-size:10px; font-weight:700; color:#A2A6AA; letter-spacing:0.08em; display:block; margin-bottom:5px; }
  .bk-input { width:100%; padding:11px 14px; border:1.5px solid #D7DADE; border-radius:10px; font-size:13px; font-family:inherit; transition:border-color 0.2s; background:#fff; }
  .bk-input:focus { outline:none; border-color:#4D4E8D; box-shadow:0 0 0 3px rgba(77,78,141,0.1); }
  .bk-select { width:100%; padding:11px 14px; border:1.5px solid #D7DADE; border-radius:10px; font-size:13px; font-family:inherit; background:#fff; cursor:pointer; }
  .bk-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .bk-grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:14px; }
  @media(max-width:580px){ .bk-grid-2,.bk-grid-3 { grid-template-columns:1fr; } }
  .bk-full { grid-column:1/-1; }
  .bk-field { margin-bottom:14px; }
  .bk-field:last-child { margin-bottom:0; }

  /* TABLE */
  .bk-table { width:100%; border-collapse:collapse; }
  .bk-table th { font-size:10px; font-weight:700; color:#A2A6AA; letter-spacing:0.08em; padding:8px 12px; text-align:left; border-bottom:2px solid #E2E4E7; }
  .bk-table td { padding:10px 8px; border-bottom:1px solid #EEEFF1; font-size:13px; color:#151715; vertical-align:middle; word-break:break-word; }
  .bk-table tr:last-child td { border-bottom:none; }
  .bk-table tr:hover td { background:#F7F8F9; }
  .bk-remove-btn { background:none; border:1px solid #E2E4E7; border-radius:6px; padding:4px 8px; font-size:11px; color:#E24B4A; cursor:pointer; transition:all 0.15s; font-family:inherit; white-space:nowrap; flex-shrink:0; }
  .bk-remove-btn:hover { background:#FDF2F2; border-color:#E24B4A; }
  .bk-price { font-family:'Plus Jakarta Sans',sans-serif; font-size:18px; font-weight:600; color:#151715; }
  .bk-total-row { background:#F5F6F7; }
  .bk-total-row td { font-weight:700; padding:14px 12px; }
  .bk-total-price { font-family:'Plus Jakarta Sans',sans-serif; font-size:28px; font-weight:700; color:#151715; }

  /* ── MOBILE CART TABLE — collapse 4 columns to card layout on small screens ── */
  @media(max-width:480px){
    .bk-table thead { display:none; }
    .bk-table, .bk-table tbody, .bk-table tfoot { display:block; }
    .bk-table tr { display:block; background:#fff; border:1px solid #E2E4E7; border-radius:12px; margin-bottom:10px; padding:12px 14px; }
    .bk-table td { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #EEEFF1; font-size:12px; gap:8px; }
    .bk-table td:last-child { border-bottom:none; padding-top:10px; }
    .bk-total-row { background:#F5F6F7; border-radius:12px; }
    .bk-total-price { font-size:22px; }
  }

  /* SUMMARY REVIEW */
  .bk-review-row { display:flex; justify-content:space-between; padding:10px 0; border-bottom:1px solid #EEEFF1; font-size:13px; }
  .bk-review-row:last-child { border-bottom:none; }
  .bk-review-label { color:#5C6065; }
  .bk-review-value { color:#151715; font-weight:500; text-align:right; max-width:60%; }

  /* BUTTONS */
  .bk-btn-primary { width:100%; padding:14px; border-radius:12px; border:none; background:linear-gradient(135deg,#4D4E8D,#41427a); color:#fff; font-size:15px; font-weight:700; cursor:pointer; font-family:inherit; letter-spacing:0.04em; transition:all 0.2s; }
  .bk-btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 24px rgba(21,23,21,0.3); }
  .bk-btn-primary:disabled { opacity:0.5; cursor:not-allowed; }
  .bk-btn-outline { width:100%; padding:12px; border-radius:12px; border:1.5px solid #D7DADE; background:#fff; color:#151715; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit; transition:all 0.2s; }
  .bk-btn-outline:hover { border-color:#4D4E8D; }
  .bk-btn-pay { width:100%; padding:16px; border-radius:12px; border:none; background:linear-gradient(135deg,#5E8A72,#1A4A2E); color:#fff; font-size:16px; font-weight:700; cursor:pointer; font-family:inherit; letter-spacing:0.04em; transition:all 0.2s; display:flex; align-items:center; justify-content:center; gap:10px; }
  .bk-btn-pay:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 24px rgba(45,122,79,0.4); }
  .bk-btn-pay:disabled { opacity:0.5; cursor:not-allowed; }
  .bk-btn-row { display:flex; gap:12px; margin-top:8px; }
  .bk-btn-row .bk-btn-outline { flex:1; }
  .bk-btn-row .bk-btn-primary { flex:2; }

  /* NOTICE */
  .bk-notice { background:linear-gradient(135deg,#EBF4FF,#F0F8FF); border:1px solid rgba(24,95,165,0.2); border-radius:12px; padding:14px 18px; margin-bottom:16px; display:flex; gap:10px; }
  .bk-notice-text { font-size:12px; color:#3A3D40; line-height:1.7; }

  /* THANK YOU */
  .bk-thankyou { text-align:center; padding:40px 24px; }
  .bk-thankyou-icon { font-size:52px; margin-bottom:14px; }
  .bk-thankyou-title { font-family:'Plus Jakarta Sans',sans-serif; font-size:30px; color:#151715; font-weight:600; margin-bottom:8px; }
  .bk-thankyou-sub { font-size:13px; color:#5C6065; line-height:1.8; margin-bottom:24px; max-width:480px; margin-left:auto; margin-right:auto; }
  .bk-thankyou-btns { display:flex; gap:12px; justify-content:center; flex-wrap:wrap; margin-bottom:28px; }
  .bk-thankyou-track { background:linear-gradient(135deg,#4D4E8D,#41427a); color:#fff; border:none; padding:14px 28px; border-radius:12px; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; text-decoration:none; display:inline-block; transition:all 0.2s; }
  .bk-thankyou-track:hover { transform:translateY(-2px); }
  .bk-thankyou-close { background:#fff; color:#151715; border:1.5px solid #D7DADE; padding:14px 28px; border-radius:12px; font-size:14px; font-weight:600; cursor:pointer; font-family:inherit; transition:all 0.2s; text-decoration:none; display:inline-block; }
  .bk-thankyou-close:hover { border-color:#4D4E8D; }

  /* EMPTY */
  .bk-empty { text-align:center; padding:48px 24px; }

  /* FOOTER */
  .bk-footer { background:#4D4E8D; padding:20px 24px; text-align:center; }
  .bk-footer-copy { font-size:11px; color:rgba(255,255,255,0.3); }

  /* PAYMENT SECTION */
  .bk-payment-box { background:linear-gradient(135deg,#F8FDF9,#EAF2EE); border:2px solid rgba(45,122,79,0.25); border-radius:16px; padding:24px; margin-bottom:16px; }
  .bk-payment-total { display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; }
  .bk-payment-label { font-size:13px; color:#5C6065; }
  .bk-payment-amount { font-family:'Plus Jakarta Sans',sans-serif; font-size:36px; color:#151715; font-weight:700; }
  .bk-payment-sub { font-size:11px; color:#4A7A5C; }
  .bk-secure-badge { display:flex; align-items:center; gap:6px; font-size:11px; color:#5C6065; justify-content:center; margin-top:10px; }
`

// ── STEP INDICATOR ────────────────────────────────────────────
function Stepper({ current }) {
  const steps = [
    { id:1, label:'CART'    },
    { id:2, label:'YOUR INFO' },
    { id:3, label:'REVIEW'  },
    { id:4, label:'CONFIRM' },
  ]
  return (
    <div className="bk-stepper" role="navigation" aria-label="Checkout steps">
      <div className="bk-stepper-inner">
        {steps.map((s, i) => {
          const status = s.id < current ? 'done' : s.id === current ? 'active' : 'pending'
          return (
            <div key={s.id} className="bk-step">
              <div className={`bk-step-dot ${status}`} aria-label={`Step ${s.id}: ${s.label}${s.id < current ? ' completed' : s.id === current ? ' current' : ''}`}>
                {s.id < current ? '✓' : s.id}
              </div>
              <div className={`bk-step-label ${status}`}>{s.label}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── MAIN COMPONENT ────────────────────────────────────────────
export default function BookingPage() {
  const navigate    = useNavigate()
  const location    = useLocation()
  const { user, profile } = useAuth()

  const [services,   setServices]   = useState([])
  const [cart,       setCart]       = useState([])
  const [step,       setStep]       = useState(1)
  const [isVet,      setIsVet]      = useState(false)
  const [witness,    setWitness]    = useState(false)
  const [processing, setProcessing] = useState(false)
  const [submitted,  setSubmitted]  = useState(null)
  const [pendingJobId, setPendingJobId] = useState(null)

  const [form, setForm] = useState({
    full_name:       '',
    email:           '',
    phone:           '',
    signing_address: '',
    signing_city:    '',
    signing_state:   'FL',
    signing_zip:     '',
    scheduled_at:    '',
    notes:           '',
  })
  function set(k, v) { setForm(f => ({ ...f, [k]:v })) }

  useEffect(() => {
    supabase.from('services').select('*').eq('show_to_individuals', true).order('name')
      .then(({ data, error }) => {
        const svcs = !error && data?.length > 0 ? data : DEFAULT_SERVICES
        setServices(svcs)
        const params = new URLSearchParams(location.search)
        const mode   = params.get('mode')
        const success = params.get('success')
        const jobId  = params.get('job_id')
        const sessionId = params.get('session_id')
        if (success === 'true' && jobId) { handlePaymentSuccess(jobId, sessionId); return }
        if (params.get('cancelled') === 'true') {
          toast.error('Payment cancelled — your cart has been saved!!')
          if (jobId) setPendingJobId(jobId)
          try { const stored = sessionStorage.getItem('lca_quote_cart'); if (stored) setCart(JSON.parse(stored)) } catch(e) {}
          return
        }
        if (mode === 'quote') {
          try { const stored = sessionStorage.getItem('lca_quote_cart'); if (stored) { setCart(JSON.parse(stored)); sessionStorage.removeItem('lca_quote_cart') } } catch(e) {}
        }
        const svcSlug = params.get('service')
        if (svcSlug && mode !== 'quote') {
          const searchName = svcSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
          const match = svcs.find(s => s.name?.toLowerCase().replace(/[^\w\s]/g,'').trim() === searchName.toLowerCase().replace(/[^\w\s]/g,'').trim())
          if (match) setCart([{ id:`direct::${match.name}`, name:match.name, categoryId:'direct' }])
        }
      })
  }, [location.search])

  useEffect(() => {
    if (profile) { set('full_name', profile.full_name || ''); set('phone', profile.phone || '') }
    if (user?.email) set('email', user.email)
  }, [profile, user])

  const NAME_MAP = {
    'Affidavit (General)':'General Affidavit','Rental Verification Form':'Rental Verification','Travel Consent Letter for Minors':'Travel Consent for a Minor','Tattoo Parental Consent (Minor)':'Tattoo Consent for a Minor','Parental Consent Form':'Parental Consent','I-9 Form':'I-9 Verification','Employment Verification Form':'Employment Verification','Consent & Release Form':'Consent & Release','Hold Harmless Agreement':'Hold Harmless','Dealer Reassignment Form':'Dealer Reassignment','Odometer Disclosure Statement':'Odometer Statement','Bill of Sale (Vehicle)':'Vehicle Bill of Sale','Title Transfer':'Vehicle Title Transfer','Power of Attorney (Vehicle)':'Vehicle Power of Attorney','Lease Agreement (Auto)':'Auto Lease','Bank Safe Deposit Box Inventory / Drilling':'Safe Deposit Box Inventory','Advance Health Directive / Living Will':'Advance Directive','Pension / Retirement Forms':'Retirement Forms','Signature Witnessing':'Signature Card','Banking Forms':'Bank Form','School Documents':'School Form','VIN / Vehicle Identification Statement':'VIN Verification (where allowed)','International Document Statement':'International Statement','Prenuptial / Postnuptial Agreement':'Wedding-Prenup / Postnup','Guardianship / Custodian Affidavit':'Guardianship','Proof of Life / Life Certificate':'Proof of Life / Pension Life Certificate','Medical Authorization Form':'Medical Authorization','Insurance Claims Form':'Insurance Claims','Finance Documents Package':'Finance Documents Package','Vehicle Purchase Contract':'Dealership Purchase Package','Bill of Sale':'Vehicle Bill of Sale','Private Party Agreement':'Private Party Agreement','Adoption Documents':'Adoption Documents','Court Documents':'Court Documents','DMV Forms':'DMV Forms','Passport Forms':'Passport Forms','Lease Agreement':'Lease Agreement','Certified Copies':'Certified Copies','Apostille':'Apostille-Standard','Wedding Ceremony Officiation':'Wedding-Simple Ceremony','Living Will':'Living Will','Power of Attorney':'Power of Attorney','Jurat / Oath':'Jurat / Oath','Acknowledgement':'Acknowledgement',
  }

  const priceMap = useMemo(() => {
    const map = {}
    services.forEach(s => {
      if (!s.name) return
      const indPrice = s.individual_price ?? s.client_price ?? 0
      map[s.name.toLowerCase().trim()] = indPrice
      map[s.name.toLowerCase().replace(/[^a-z0-9]/g, '')] = indPrice
    })
    return map
  }, [services])

  function normalize(str) { return (str || '').toLowerCase().replace(/[^a-z0-9]/g, '') }

  function getPrice(svcName) {
    if (!svcName || services.length === 0) return 0
    const priceOf = s => (s?.individual_price ?? s?.client_price ?? 0)
    const mappedName = NAME_MAP[svcName]
    if (mappedName) { const match = services.find(s => s.name?.toLowerCase().trim() === mappedName.toLowerCase().trim()); if (match) return priceOf(match) }
    const exactMatch = services.find(s => s.name?.toLowerCase().trim() === svcName.toLowerCase().trim())
    if (exactMatch) return priceOf(exactMatch)
    const normTarget = normalize(svcName)
    const normMatch = services.find(s => normalize(s.name) === normTarget)
    if (normMatch) return priceOf(normMatch)
    let bestMatch = null; let bestScore = 0
    services.forEach(s => {
      const sNorm = normalize(s.name); const tWords = normTarget.match(/.{3,}/g) || []
      const overlap = tWords.filter(w => sNorm.includes(w)).length
      const score = overlap / Math.max(tWords.length, 1) * 100
      if (score > bestScore && score >= 50) { bestScore = score; bestMatch = s }
    })
    return priceOf(bestMatch)
  }

  const cartWithPrices = cart.map((item, index) => {
    const basePrice  = getPrice(item.name)
    const isExcluded = BUNDLE_EXCLUDE.some(ex => item.name.toLowerCase().includes(ex.toLowerCase()))
    const discountablesBefore = cart.slice(0, index).filter(i => !BUNDLE_EXCLUDE.some(ex => i.name.toLowerCase().includes(ex.toLowerCase()))).length
    const discountPct = isExcluded ? 0 : Math.min(discountablesBefore * 5, 100)
    const discountAmt = basePrice > 0 ? Math.round(basePrice * (discountPct / 100) * 100) / 100 : 0
    return { ...item, basePrice, discountPct, discountAmt, price: basePrice - discountAmt, isExcluded }
  })

  const subtotal    = cartWithPrices.reduce((s, i) => s + (i.price || 0), 0)
  const vetDiscount = isVet ? Math.round(subtotal * 0.10) : 0
  const witnessFee  = witness ? 35 : 0
  const serviceNet  = subtotal - vetDiscount
  const total       = serviceNet + witnessFee
  // 👥 Witness add-on: client +$35 → notary +$25, NSV +$10 (base service stays 75/25)
  const notaryPay   = (serviceNet > 0 ? Math.round(serviceNet * 0.75) : 0) + (witness ? 25 : 0)
  const lcaCut      = total - notaryPay
  const totalSaved  = cartWithPrices.reduce((s, i) => s + (i.discountAmt || 0), 0) + vetDiscount

  function removeFromCart(id) { setCart(prev => prev.filter(i => i.id !== id)) }

  async function handlePaymentSuccess(jobId, sessionId) {
    try {
      const { data: job } = await supabase.from('jobs').select('*').eq('id', jobId).single()
      if (job) {
        setSubmitted(job); setStep(4); sessionStorage.removeItem('lca_quote_cart')
        const el = document.getElementById('lca-live-region')
        if (el) el.textContent = `Payment confirmed!! Your order number is ${job.job_number}!!`
        try {
          await supabase.from('jobs').update({ status: 'awaiting_assignment', paid_at: new Date().toISOString(), client_paid_at: new Date().toISOString() }).eq('id', jobId)
          await supabase.from('job_status_events').insert({ job_id: jobId, status: 'awaiting_assignment', from_status: 'new', note: '💳 Payment confirmed via Stripe — awaiting notary assignment', created_by_role: 'system' })
        } catch(statusErr) { console.error('Status update error:', statusErr) }
        try {
          const fullAddress = [job.signing_address, job.signing_city, job.signing_state].filter(Boolean).join(', ')
          const apptStr = job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : null
          await Promise.all([
            sendAdminAlert({ client_name: job.borrower_name, client_phone: job.borrower_phone, client_email: job.borrower_email, service_name: job.signing_type, amount: job.total_fee, address: fullAddress, appointment: apptStr }),
            sendClientConfirmation({ client_name: job.borrower_name, client_email: job.borrower_email, service_name: job.signing_type, amount: job.total_fee, job_number: job.job_number, address: fullAddress, appointment: apptStr })
          ])
        } catch(emailErr) { console.error('Email notification error:', emailErr) }
      }
    } catch(err) { console.error('Payment success handler error:', err); setStep(4) }
  }

  async function proceedToPayment() {
    if (cart.length === 0) { toast.error('Your cart is empty!!'); return }
    if (!form.full_name || !form.signing_address || !form.email) { toast.error('Please fill in all required fields!!'); return }
    setProcessing(true)
    try {
      const serviceList    = cartWithPrices.map(i => i.name).join(', ')
      const primaryService = cartWithPrices[0]?.name || 'Notary Services'
      const { data: job, error } = await supabase.from('jobs').insert({
        job_type: 'individual', scan_backs_required: false, individual_user_id: user?.id || null, client_id: null,
        borrower_name: form.full_name, borrower_phone: form.phone || null, borrower_email: form.email,
        signing_type: cartWithPrices.length === 1 ? primaryService : `Multiple Services (${cartWithPrices.length})`,
        signing_address: form.signing_address, signing_city: form.signing_city, signing_state: form.signing_state, signing_zip: form.signing_zip || null,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        special_instructions: [cartWithPrices.length > 1 ? `SERVICES: ${serviceList}` : null, form.notes || null, isVet ? '🎖️ VETERAN — 10% discount applied' : null].filter(Boolean).join('\n') || null,
        total_fee: total, notary_pay: notaryPay, lca_cut: lcaCut, witness_required: witness, status: 'new',
      }).select().single()
      if (error) throw error
      await supabase.from('job_payments').insert({ job_id: job.id, client_invoice_amount: total, notary_pay_amount: notaryPay, client_payment_status: 'pending' })
      await supabase.from('job_status_events').insert({ job_id: job.id, status: 'new', from_status: null, created_by_role: user ? 'client' : 'guest', created_by: user?.id || null, note: `Quote submitted: ${serviceList}` })
      sessionStorage.setItem('lca_quote_cart', JSON.stringify(cart))
      let stripeItems = cartWithPrices.map(i => ({ name: i.name, price: i.price, quantity: 1 }))
      // 🎖️ VETERAN DISCOUNT — bake it into the line items so Stripe charges the discounted total.
      // (A separate negative line item gets stripped by the price > 0 filter below — that was the bug!!)
      if (isVet && vetDiscount > 0 && subtotal > 0) {
        const factor = total / subtotal
        stripeItems = stripeItems.map(i => ({ ...i, price: Math.round(i.price * factor * 100) / 100 }))
        // Reconcile any sub-cent rounding drift onto the last item so the sum equals `total` exactly
        const summed = stripeItems.reduce((s, i) => s + i.price, 0)
        const drift  = Math.round((total - summed) * 100) / 100
        if (drift !== 0 && stripeItems.length > 0) {
          const last = stripeItems.length - 1
          stripeItems[last].price = Math.round((stripeItems[last].price + drift) * 100) / 100
        }
      }
      const response = await fetch('/.netlify/functions/create-checkout', {
        method: 'POST', headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ items: stripeItems.filter(i => i.price > 0), total, customerEmail: form.email, customerName: form.full_name, jobId: job.id, successUrl: `${window.location.origin}/book?success=true&session_id={CHECKOUT_SESSION_ID}&job_id=${job.id}`, cancelUrl: `${window.location.origin}/book?cancelled=true&job_id=${job.id}` }),
      })
      const { sessionUrl, error: stripeError } = await response.json()
      if (stripeError) throw new Error(stripeError)
      window.location.href = sessionUrl
    } catch(err) { toast.error(err.message || 'Something went wrong!! Please try again.'); setProcessing(false) }
  }

  // ── RENDER: STEP 4 — CONFIRMATION ───────────────────────────
  if (step === 4) {
    return (
      <>
        <style>{STYLES}</style>
        <div id="lca-live-region" aria-live="assertive" aria-atomic="true" style={{ position:'absolute', width:1, height:1, overflow:'hidden', clip:'rect(0,0,0,0)' }} />
        <div className="bk-page">
          <nav className="bk-nav" role="navigation">
            <Link to="/" className="bk-nav-brand">Notary Signing Vault<span className="bk-nav-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span></Link>
          </nav>
          <Stepper current={4} />
          <main id="main-content" tabIndex={-1}>
            <div className="bk-container">
              <div className="bk-thankyou">
                <div className="bk-thankyou-icon" aria-hidden="true">🎉</div>
                <h1 className="bk-thankyou-title">Payment Confirmed!!</h1>
                <p className="bk-thankyou-sub">
                  {submitted ? <>Your order <strong>#{submitted.job_number}</strong> has been received and paid!!</> : 'Your order has been received!!'}<br />
                  Our team has been notified immediately and is finding the best qualified notary for your needs!!<br /><br />
                  {form.email && <>A confirmation has been sent to <strong>{form.email}</strong>!!</>}
                </p>
                {total > 0 && (
                  <div style={{ background:'#EAF2EE', border:'1px solid rgba(45,122,79,0.2)', borderRadius:12, padding:'16px 24px', marginBottom:24, display:'inline-block' }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#5E8A72', letterSpacing:'0.06em', marginBottom:4 }}>AMOUNT PAID</div>
                    <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:32, color:'#151715', fontWeight:600 }}>${total}</div>
                    <div style={{ fontSize:11, color:'#4A7A5C' }}>✅ Payment received · All-inclusive</div>
                  </div>
                )}
                <div className="bk-thankyou-btns">
                  {submitted && <Link to={`/order/track/${submitted.id}`} className="bk-thankyou-track" aria-label={`Track order ${submitted.job_number}`}>📋 Track My Order →</Link>}
                  <button className="bk-thankyou-close" onClick={() => window.close() || navigate('/')} aria-label="Close this window">Close Window</button>
                </div>
                <div style={{ background:'#F5F6F7', borderRadius:14, padding:'20px 24px', textAlign:'left', maxWidth:480, margin:'0 auto' }}>
                  <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, color:'#151715', marginBottom:14 }}>What happens next?</div>
                  {[
                    { icon:'👀', text:'Our team reviews your request immediately' },
                    { icon:'👤', text:'We assign the best qualified notary in your area' },
                    { icon:'📞', text:'Your notary contacts you to confirm the appointment' },
                    { icon:'✍️', text:'Your signing is completed at your location' },
                    { icon:'📧', text:'You\'ll receive a follow-up email to share your experience' },
                  ].map((s, i) => (
                    <div key={i} style={{ display:'flex', gap:12, marginBottom:10 }}>
                      <span style={{ fontSize:16, flexShrink:0 }} aria-hidden="true">{s.icon}</span>
                      <span style={{ fontSize:13, color:'#3A3D40', lineHeight:1.6 }}>{s.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </main>
          <footer className="bk-footer"><p className="bk-footer-copy">© 2026 Notary Signing Vault, LLC · DBA Nationwide Notary Professionals Network</p></footer>
        </div>
      </>
    )
  }

  // ── RENDER: EMPTY CART ────────────────────────────────────────
  if (cart.length === 0) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="bk-page">
          <nav className="bk-nav" role="navigation">
            <Link to="/" className="bk-nav-brand">Notary Signing Vault<span className="bk-nav-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span></Link>
            <Link to="/services" className="bk-nav-back">← Back to Services</Link>
          </nav>
          <Stepper current={1} />
          <main id="main-content" tabIndex={-1}>
            <div className="bk-container">
              <div className="bk-empty">
                <div style={{ fontSize:40, marginBottom:12 }} aria-hidden="true">🛒</div>
                <h1 style={{ fontSize:20, fontWeight:600, color:'#151715', marginBottom:6 }}>Your cart is empty!!</h1>
                <p style={{ fontSize:13, color:'#5C6065', marginBottom:20 }}>Browse our services and add what you need to your quote!!</p>
                <Link to="/services" style={{ display:'inline-block', background:'linear-gradient(135deg,#4D4E8D,#41427a)', color:'#fff', padding:'14px 32px', borderRadius:12, fontWeight:700, fontSize:14, textDecoration:'none', letterSpacing:'0.04em' }}>
                  Browse Services →
                </Link>
              </div>
            </div>
          </main>
        </div>
      </>
    )
  }

  return (
    <>
      <style>{STYLES}</style>
      <div id="lca-live-region" aria-live="assertive" aria-atomic="true" style={{ position:'absolute', width:1, height:1, overflow:'hidden', clip:'rect(0,0,0,0)' }} />
      <a href="#main-content" className="bk-skip">Skip to main content</a>
      <div className="bk-page">
        <nav className="bk-nav" role="navigation" aria-label="Main navigation">
          <Link to="/" className="bk-nav-brand" aria-label="Notary Signing Vault — Home">
            Notary Signing Vault<span className="bk-nav-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
          </Link>
          <Link to="/services" className="bk-nav-back">← Back to Services</Link>
        </nav>
        <Stepper current={step} />
        <main id="main-content" tabIndex={-1}>
          <div className="bk-container">

            {/* ── STEP 1 — CART ── */}
            {step === 1 && (
              <>
                <h1 className="bk-page-title">Your Quote</h1>
                <p className="bk-page-sub">Review your selected services and apply any discounts before continuing!!</p>
                <section className="bk-card" aria-labelledby="cart-heading">
                  <div className="bk-card-header">
                    <h2 className="bk-card-title" id="cart-heading">📋 Selected Services</h2>
                    <Link to="/services" style={{ fontSize:12, color:'#4D4E8D', textDecoration:'none', fontWeight:600 }}>+ Add More</Link>
                  </div>
                  <div style={{ overflowX:'auto' }}>
                    <table className="bk-table" role="table" aria-label="Selected services and prices">
                      <thead>
                        <tr>
                          <th scope="col">Service</th>
                          <th scope="col">Price</th>
                          <th scope="col">Bundle Savings</th>
                          <th scope="col"><span style={{ position:'absolute', width:1, height:1, overflow:'hidden', clip:'rect(0,0,0,0)' }}>Actions</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {cartWithPrices.map((item, i) => (
                          <tr key={item.id}>
                            <td style={{ fontWeight:500 }}>
                              {item.name}
                              {item.isExcluded && <span style={{ fontSize:10, color:'#A2A6AA', marginLeft:6 }}>(full price)</span>}
                            </td>
                            <td>
                              {item.basePrice > 0 ? (
                                <>
                                  {item.discountAmt > 0 && <span style={{ fontSize:11, color:'#A2A6AA', textDecoration:'line-through', marginRight:6 }}>${item.basePrice}</span>}
                                  <span className="bk-price">${item.price}</span>
                                </>
                              ) : <span style={{ fontSize:12, color:'#A2A6AA' }}>Price on request</span>}
                            </td>
                            <td>
                              {item.discountAmt > 0
                                ? <span style={{ fontSize:12, fontWeight:600, color:'#5E8A72' }}>🎉 -{item.discountPct}% (-${item.discountAmt})</span>
                                : <span style={{ fontSize:11, color:'#A2A6AA' }}>{i === 0 ? 'Full price' : '—'}</span>}
                            </td>
                            <td>
                              <button type="button" className="bk-remove-btn" onClick={() => removeFromCart(item.id)} aria-label={`Remove ${item.name} from quote`}>Remove</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {totalSaved > 0 && (
                          <tr style={{ background:'#EAF2EE' }}>
                            <td colSpan={4} style={{ padding:'10px 12px', fontSize:13, color:'#5E8A72', fontWeight:600 }}>
                              🎉 You're saving <strong>${totalSaved.toFixed(2)}</strong> with your bundle!!
                            </td>
                          </tr>
                        )}
                        {isVet && vetDiscount > 0 && (
                          <tr>
                            <td colSpan={4} style={{ padding:'10px 12px', fontSize:13, color:'#5E8A72' }}>
                              🎖️ Veteran Discount (10%) <strong>-${vetDiscount}</strong>
                            </td>
                          </tr>
                        )}
                        {witness && (
                          <tr>
                            <td colSpan={4} style={{ padding:'10px 12px', fontSize:13, color:'#151715' }}>
                              👥 Additional Witness <strong>+$35</strong>
                            </td>
                          </tr>
                        )}
                        <tr className="bk-total-row">
                          <td colSpan={2} style={{ fontSize:13, color:'#5C6065' }}>Total ({cartWithPrices.length} service{cartWithPrices.length !== 1 ? 's' : ''})</td>
                          <td colSpan={2}>
                            <span className="bk-total-price" aria-label={`Total: $${total}`}>${total}</span>
                            <span style={{ fontSize:11, color:'#5C6065', marginLeft:8 }}>All-inclusive</span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div style={{ padding:'14px 20px', borderTop:'1px solid #EEEFF1', display:'flex', alignItems:'center', gap:10 }}>
                    <input type="checkbox" id="vet-check" checked={isVet} onChange={e => setIsVet(e.target.checked)} style={{ width:16, height:16, cursor:'pointer', accentColor:'#4D4E8D' }} />
                    <label htmlFor="vet-check" style={{ fontSize:13, color:'#151715', cursor:'pointer' }}>
                      🎖️ I am a Military Veteran or Active Duty — apply my <strong>10% discount</strong>
                    </label>
                  </div>
                  <div style={{ padding:'14px 20px', borderTop:'1px solid #EEEFF1', display:'flex', alignItems:'center', gap:10 }}>
                    <input type="checkbox" id="witness-check" checked={witness} onChange={e => setWitness(e.target.checked)} style={{ width:16, height:16, cursor:'pointer', accentColor:'#4D4E8D' }} />
                    <label htmlFor="witness-check" style={{ fontSize:13, color:'#151715', cursor:'pointer' }}>
                      👥 I need an <strong>additional witness</strong> (+$35) — we'll provide one
                    </label>
                  </div>
                </section>
                <button type="button" className="bk-btn-primary" onClick={() => setStep(2)} disabled={cart.length === 0} aria-label="Continue to your information">
                  Continue to Your Info →
                </button>
              </>
            )}

            {/* ── STEP 2 — YOUR INFO ── */}
            {step === 2 && (
              <>
                <h1 className="bk-page-title">Your Information</h1>
                <p className="bk-page-sub">Fill in your details so we can confirm your appointment!!</p>
                <section className="bk-card" aria-labelledby="info-heading">
                  <div className="bk-card-header"><h2 className="bk-card-title" id="info-heading">👤 Contact Details</h2></div>
                  <div className="bk-card-body">
                    <div className="bk-grid-2">
                      <div className="bk-field">
                        <label className="bk-label" htmlFor="bk-name">FULL NAME <span style={{ color:'#E24B4A' }}>*</span></label>
                        <input id="bk-name" className="bk-input" value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Jane Smith" required />
                      </div>
                      <div className="bk-field">
                        <label className="bk-label" htmlFor="bk-phone">PHONE NUMBER</label>
                        <input id="bk-phone" className="bk-input" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(904) 555-0100" />
                      </div>
                      <div className="bk-field bk-full">
                        <label className="bk-label" htmlFor="bk-email">EMAIL ADDRESS <span style={{ color:'#E24B4A' }}>*</span></label>
                        <input id="bk-email" className="bk-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@email.com" required />
                        <span style={{ fontSize:11, color:'#A2A6AA', marginTop:4, display:'block' }}>We'll send your confirmation and tracking link here!!</span>
                      </div>
                    </div>
                  </div>
                </section>
                <section className="bk-card" aria-labelledby="appt-heading">
                  <div className="bk-card-header"><h2 className="bk-card-title" id="appt-heading">📅 Appointment Details</h2></div>
                  <div className="bk-card-body">
                    <div className="bk-field" style={{ marginBottom:14 }}>
                      <label className="bk-label" htmlFor="bk-addr">SIGNING ADDRESS <span style={{ color:'#E24B4A' }}>*</span></label>
                      <AddressAutocomplete inputClass="bk-input" value={form.signing_address} onChange={v => set('signing_address', v)}
                        onSelect={({ address, city, state, zip }) => { set('signing_address', address); if (city) set('signing_city', city); if (state) set('signing_state', state); if (zip) set('signing_zip', zip); toast.success('📍 Address auto-filled!!') }}
                        placeholder="Start typing your address..." />
                    </div>
                    <div className="bk-grid-3">
                      <div className="bk-field">
                        <label className="bk-label" htmlFor="bk-city">CITY</label>
                        <input id="bk-city" className="bk-input" value={form.signing_city} onChange={e => set('signing_city', e.target.value)} placeholder="Jacksonville" />
                      </div>
                      <div className="bk-field">
                        <label className="bk-label" htmlFor="bk-state">STATE</label>
                        <input id="bk-state" className="bk-input" value={form.signing_state} onChange={e => set('signing_state', e.target.value)} placeholder="FL" maxLength={2} />
                      </div>
                      <div className="bk-field">
                        <label className="bk-label" htmlFor="bk-zip">ZIP CODE</label>
                        <input id="bk-zip" className="bk-input" value={form.signing_zip} onChange={e => set('signing_zip', e.target.value)} placeholder="32202" maxLength={5} />
                      </div>
                    </div>
                    <div className="bk-field" style={{ marginTop:14 }}>
                      <label className="bk-label" htmlFor="bk-dt">PREFERRED DATE & TIME</label>
                      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
                        <div style={{ flex:'1 1 180px', minWidth:0 }}>
                          <span style={{ fontSize:10, color:'#A2A6AA', fontWeight:600, letterSpacing:'0.06em', display:'block', marginBottom:6 }}>📅 DATE</span>
                          <input id="bk-dt" className="bk-input" type="date"
                            value={form.scheduled_at ? form.scheduled_at.split('T')[0] : ''}
                            min={new Date().toISOString().slice(0,10)}
                            onChange={e => { const time = (form.scheduled_at && form.scheduled_at.includes('T')) ? form.scheduled_at.split('T')[1] : '12:00'; set('scheduled_at', e.target.value ? `${e.target.value}T${time}` : '') }} />
                        </div>
                        <div style={{ flex:'1 1 180px', minWidth:0 }}>
                          <span style={{ fontSize:10, color:'#A2A6AA', fontWeight:600, letterSpacing:'0.06em', display:'block', marginBottom:6 }}>🕒 TIME</span>
                          <input className="bk-input" type="time"
                            value={form.scheduled_at && form.scheduled_at.includes('T') ? form.scheduled_at.split('T')[1].slice(0,5) : ''}
                            onChange={e => { const date = form.scheduled_at ? form.scheduled_at.split('T')[0] : new Date().toISOString().split('T')[0]; set('scheduled_at', e.target.value ? `${date}T${e.target.value}` : '') }} />
                        </div>
                      </div>
                      <span style={{ fontSize:11, color:'#A2A6AA', marginTop:4, display:'block' }}>Our team will confirm availability with you!!</span>
                    </div>
                    <div className="bk-field" style={{ marginTop:14 }}>
                      <label className="bk-label" htmlFor="bk-notes">SPECIAL INSTRUCTIONS (optional)</label>
                      <textarea id="bk-notes" className="bk-input" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any special instructions, access codes, or questions..." rows={3} style={{ resize:'none' }} />
                    </div>
                  </div>
                </section>
                <div className="bk-btn-row">
                  <button type="button" className="bk-btn-outline" onClick={() => setStep(1)}>← Back</button>
                  <button type="button" className="bk-btn-primary" onClick={() => { if (!form.full_name || !form.signing_address || !form.email) { toast.error('Please fill in all required fields!!'); return } setStep(3) }} aria-label="Review your order">
                    Review My Order →
                  </button>
                </div>
              </>
            )}

            {/* ── STEP 3 — ORDER REVIEW ── */}
            {step === 3 && (
              <>
                <h1 className="bk-page-title">Review Your Order</h1>
                <p className="bk-page-sub">Please review everything carefully before proceeding to payment!!</p>
                <section className="bk-card" aria-labelledby="review-services">
                  <div className="bk-card-header">
                    <h2 className="bk-card-title" id="review-services">📋 Services</h2>
                    <button type="button" onClick={() => setStep(1)} style={{ fontSize:11, color:'#4D4E8D', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>Edit</button>
                  </div>
                  <div className="bk-card-body" style={{ padding:'0 0 4px' }}>
                    {cartWithPrices.map((item, i) => (
                      <div key={item.id} className="bk-review-row" style={{ padding:'12px 20px' }}>
                        <span className="bk-review-label">{item.name}</span>
                        <span className="bk-review-value">
                          {item.discountAmt > 0 && <span style={{ fontSize:11, color:'#A2A6AA', textDecoration:'line-through', marginRight:6 }}>${item.basePrice}</span>}
                          <strong>${item.price}</strong>
                          {item.discountPct > 0 && <span style={{ fontSize:10, color:'#5E8A72', marginLeft:6 }}>(-{item.discountPct}%)</span>}
                        </span>
                      </div>
                    ))}
                    {isVet && vetDiscount > 0 && (
                      <div className="bk-review-row" style={{ padding:'12px 20px', background:'#F0FDF4' }}>
                        <span style={{ color:'#5E8A72', fontSize:13 }}>🎖️ Veteran Discount (10%)</span>
                        <span style={{ color:'#5E8A72', fontWeight:600 }}>-${vetDiscount}</span>
                      </div>
                    )}
                  </div>
                </section>
                <section className="bk-card" aria-labelledby="review-contact">
                  <div className="bk-card-header">
                    <h2 className="bk-card-title" id="review-contact">👤 Your Information</h2>
                    <button type="button" onClick={() => setStep(2)} style={{ fontSize:11, color:'#4D4E8D', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>Edit</button>
                  </div>
                  <div className="bk-card-body">
                    {[
                      { label:'Name',    value: form.full_name },
                      { label:'Email',   value: form.email },
                      { label:'Phone',   value: form.phone || '—' },
                      { label:'Address', value: `${form.signing_address}, ${form.signing_city}, ${form.signing_state} ${form.signing_zip}` },
                      { label:'Date',    value: form.scheduled_at ? new Date(form.scheduled_at).toLocaleString() : 'Flexible — to be confirmed' },
                      { label:'Notes',   value: form.notes || '—' },
                    ].map(row => (
                      <div key={row.label} className="bk-review-row">
                        <span className="bk-review-label">{row.label}</span>
                        <span className="bk-review-value">{row.value}</span>
                      </div>
                    ))}
                  </div>
                </section>
                <div className="bk-payment-box" role="region" aria-labelledby="payment-heading">
                  <div className="bk-payment-total">
                    <div>
                      <h2 id="payment-heading" style={{ fontSize:13, color:'#4A7A5C', fontWeight:600, marginBottom:4 }}>TOTAL DUE TODAY</h2>
                      <div className="bk-payment-amount" aria-label={`Total: $${total}`}>${total}</div>
                      <div className="bk-payment-sub">All-inclusive · No hidden fees · No travel charges</div>
                    </div>
                    {totalSaved > 0 && (
                      <div style={{ background:'#5E8A72', color:'#fff', padding:'8px 14px', borderRadius:10, textAlign:'center' }}>
                        <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.06em' }}>YOU SAVE</div>
                        <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:22, fontWeight:700 }}>${totalSaved.toFixed(2)}</div>
                      </div>
                    )}
                  </div>
                  <div className="bk-notice">
                    <span aria-hidden="true">ℹ️</span>
                    <p className="bk-notice-text">Once payment is confirmed, our team is notified immediately to assign a certified notary to your case. You'll receive a confirmation email right away!!</p>
                  </div>
                  <button type="button" className="bk-btn-pay" onClick={proceedToPayment} disabled={processing} aria-label={`Pay $${total} securely with Stripe`}>
                    {processing ? <>⏳ Preparing secure checkout...</> : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <rect x="1" y="4" width="22" height="16" rx="3" ry="3" stroke="white" strokeWidth="2" fill="none"/>
                          <line x1="1" y1="10" x2="23" y2="10" stroke="white" strokeWidth="2"/>
                        </svg>
                        Pay ${total} Securely →
                      </>
                    )}
                  </button>
                  <div className="bk-secure-badge" aria-label="Secure payment powered by Stripe">
                    🔒 Secure payment powered by Stripe · Your card info is never stored by NSV
                  </div>
                </div>
                <div className="bk-btn-row" style={{ marginTop:8 }}>
                  <button type="button" className="bk-btn-outline" onClick={() => setStep(2)}>← Back</button>
                </div>
                <p style={{ fontSize:11, color:'#A2A6AA', textAlign:'center', marginTop:16, lineHeight:1.6 }}>
                  By paying, you agree to our <Link to="/terms" style={{ color:'#4D4E8D' }}>Terms of Service</Link> and <Link to="/privacy" style={{ color:'#4D4E8D' }}>Privacy Policy</Link>.
                </p>
              </>
            )}

          </div>
        </main>
        <footer className="bk-footer" role="contentinfo">
          <p className="bk-footer-copy">© 2026 Notary Signing Vault, LLC · DBA Nationwide Notary Professionals Network</p>
        </footer>
      </div>
    </>
  )
}
